import React, {useContext} from "react"
import {
    Badge,
    Box,
    Button,
    Divider,
    IconButton,
    Menu,
    Typography,
    styled,
} from "@mui/material"
import {useTheme} from "@emotion/react"
import {CircleAlert, CircleCheck, Info, TriangleAlert, X} from "lucide-react"
import {DataContext} from "../context/DataContextProvider"
import {clearNotifications, removeNotification} from "../context/notification-helpers"

const typeIconMap = {
    info: Info,
    success: CircleCheck,
    warning: TriangleAlert,
    error: CircleAlert,
    default: Info,
}

const QButton = styled(IconButton)(({theme}) =>
    theme.NOTIFICATIONCENTER.qButton(theme),
)

const NotificationCenter = () => {
    const {data, setData} = useContext(DataContext)
    const theme = useTheme()
    const styles = theme.NOTIFICATIONCENTER
    const notifications = data?.notifications ?? []

    const [anchorEl, setAnchorEl] = React.useState(null)
    const open = Boolean(anchorEl)
    const triggerOpenMenu = (event) => setAnchorEl(event.currentTarget)
    const triggerCloseMenu = () => setAnchorEl(null)

    return (
        <Box sx={styles.wrapper}>
            <Badge
                badgeContent={notifications.length}
                color="error"
                overlap="rectangular">
                <QButton
                    onClick={triggerOpenMenu}
                    aria-label="Benachrichtigungen">
                    Q
                </QButton>
            </Badge>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={triggerCloseMenu}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{paper: {sx: styles.menuPaper}}}>
                <Box sx={styles.header}>
                    <Typography fontWeight={600}>Benachrichtigungen</Typography>
                    <Button
                        variant="tertiary"
                        size="small"
                        disabled={notifications.length === 0}
                        onClick={() => clearNotifications(setData)}>
                        alle gelesen
                    </Button>
                </Box>
                <Divider />

                {notifications.length === 0 ? (
                    <Box sx={styles.emptyState}>
                        <Typography color="text.secondary" align="center">
                            Keine Benachrichtigungen
                        </Typography>
                    </Box>
                ) : (
                    notifications.map((notification) => {
                        const Icon =
                            typeIconMap[notification.type] ?? typeIconMap.default
                        const color =
                            styles.typeColors[notification.type] ??
                            styles.typeColors.default
                        return (
                            <Box key={notification.id} sx={styles.itemWrapper}>
                                <Box sx={styles.toastCard(color)}>
                                    <Icon size={20} style={{flexShrink: 0}} />
                                    <Typography
                                        variant="body2"
                                        sx={styles.toastMessage}>
                                        {notification.message}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        aria-label="Benachrichtigung entfernen"
                                        onClick={() =>
                                            removeNotification(
                                                setData,
                                                notification.id,
                                            )
                                        }
                                        sx={styles.closeButton}>
                                        <X size={16} />
                                    </IconButton>
                                </Box>
                            </Box>
                        )
                    })
                )}
            </Menu>
        </Box>
    )
}

export default NotificationCenter
